import { useMemo, useState } from 'react';
import { DefaultOptionType } from 'rc-select/lib/Select';
import {
  GroupDescriptionType,
  SubmittedDataVersionType,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { SampleSubjectLockIcon } from '../../sample/sampleSubjectLock/SampleSubjectLockIcon';
import { colorGray } from '@atlas-frontend/design-tokens';
import styled from 'styled-components';
import {
  InputDataDescriptionInfo,
  InputDataVersionType,
  SubmittedDataStatus,
} from './types';

const EMPTY_DESCRIPTIONS: DefaultOptionType[] = [];

function getDataStatus(submittedVersion?: SubmittedDataVersionType) {
  const status = submittedVersion?.dataStatus?.code;
  switch (status) {
    case SubmittedDataStatus.DATA:
      return 'A';
    case SubmittedDataStatus.IMPUTED_DATA:
      return 'I';
    default:
      return '';
  }
}

function DescriptionLabel({
  description,
  submittedVersion,
}: {
  description: GroupDescriptionType;
  submittedVersion?: SubmittedDataVersionType;
}) {
  const dataStatus = useMemo(
    () =>
      submittedVersion?.dataStatus ? getDataStatus(submittedVersion) : null,
    [submittedVersion],
  );

  return (
    <Flex gap={5} align="center">
      <TextEllipsis style={{ width: 260 }}>
        {`${description.code} - ${description.name}`}
      </TextEllipsis>
      {(description.locked || description.sampleLocked) && (
        <SampleSubjectLockIcon
          isLocked={true}
          showBullet={description.locked && description.active}
        />
      )}
      <Fixed>
        {dataStatus && (
          <Circle>
            <span>{dataStatus}</span>
          </Circle>
        )}
      </Fixed>
    </Flex>
  );
}

export function useInputDataDescriptions(
  descriptions?: GroupDescriptionType[],
  version?: InputDataVersionType,
  descriptionId?: string,
): InputDataDescriptionInfo {
  const [description, setDescription] = useState<GroupDescriptionType>();

  const descriptionOptions = useMemo(() => {
    const options =
      descriptions
        ?.map((description) => {
          let submittedVersion: SubmittedDataVersionType | undefined;
          if (version === 'active') {
            submittedVersion = description.versions?.find(
              (version) => version.activeVersion,
            );
          } else if (version === 'draft') {
            submittedVersion = description.versions?.find(
              (version) => version.draftVersion,
            );
          }

          return { description, submittedVersion };
        })
        .filter(({ submittedVersion }) => !!submittedVersion)
        .map(
          ({ description, submittedVersion }) =>
            ({
              label: (
                <DescriptionLabel
                  description={description}
                  submittedVersion={submittedVersion}
                />
              ),
              submittedVersion,
              value: description.id,
            }) as DefaultOptionType,
        ) ?? EMPTY_DESCRIPTIONS;

    let selected: GroupDescriptionType | undefined;
    if (descriptionId) {
      const id = Number(descriptionId);
      if (isFinite(id)) {
        selected = descriptions?.find((description) => description.id === id);
      }
    }

    setDescription((prev) => {
      if (selected) {
        return selected;
      }
      const firstOption = options?.[0];
      const first = descriptions?.find(
        (description) => description.id === firstOption?.value,
      );
      if (!prev) {
        if (descriptions?.length) {
          return first;
        }
      } else if (
        !descriptions?.some((description) => description?.id === prev.id)
      ) {
        return first;
      }

      return prev;
    });

    return options;
  }, [descriptionId, descriptions, version]);

  return { description, descriptionOptions, setDescription };
}

const Circle = styled.div`
  border: 1px solid ${colorGray[6]};
  border-radius: 50%;
  color: ${colorGray[6]};
  display: flex;
  align-items: center;
  height: 20px;
  justify-content: center;
  line-height: 20px;
  margin: auto;
  width: 20px;
`;
const Fixed = styled.div`
  flex: 0 0 20px;
  position: relative;
`;
